import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import LanguageToggle from "./components/shared/LanguageToggle";

const TOKEN_KEY = "FENTASAFE_DEMO_TOKEN";
const DEMO_LOGIN_URL = process.env.REACT_APP_DEMO_LOGIN_URL;

function OtpLogin({ onSuccess }) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [msg, setMsg] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) onSuccess();
  }, [onSuccess]);

  const verifyCode = async () => {
    if (!code.trim()) return;
    setChecking(true);
    setMsg("");

    try {
      const response = await fetch(DEMO_LOGIN_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access_code: code }),
      });

      if (!response.ok) throw new Error("Backend unavailable");

      const data = await response.json();

      if (data.authenticated) {
        localStorage.setItem(TOKEN_KEY, "APPROVED");
        localStorage.setItem("FENTASAFE_USER", JSON.stringify(data));
        localStorage.setItem("FENTASAFE_ROLE", "DEMO_USER");
        onSuccess();
        return;
      }

      setMsg(t("auth.invalidCode"));
    } catch (error) {
      console.log("Backend unavailable.");
      setMsg(t("auth.verifyError"));
    } finally {
      setChecking(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") verifyCode();
  };

  return (
    <div style={styles.container}>

      {/* LANGUAGE TOGGLE */}
      <div style={styles.languageToggleRow}>
        <LanguageToggle />
      </div>

      {/* HEADER */}
      <div style={styles.lockLabel}>{t("auth.restrictedAccess")}</div>

      <h2 style={styles.title}>{t("auth.demoTitle")}</h2>

      <p style={styles.subtitle}>
        {t("auth.demoSubtitlePart1")}<br />
        {t("auth.demoSubtitlePart2")}
      </p>

      {/* TWO COLUMN INFO */}
      <div style={styles.twoColumnRow}>
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>{t("auth.whatDemoShows")}</h3>
          <ul>
            <li>{t("auth.demoShowsItem1")}</li>
            <li>{t("auth.demoShowsItem2")}</li>
            <li>{t("auth.demoShowsItem3")}</li>
            <li>{t("auth.demoShowsItem4")}</li>
            <li>{t("auth.demoShowsItem5")}</li>
          </ul>
        </div>

        <div style={styles.card}>
          <h3 style={styles.cardTitle}>{t("auth.whatDemoDoesNot")}</h3>
          <ul>
            <li>{t("auth.demoDoesNotItem1")}</li>
            <li>{t("auth.demoDoesNotItem2")}</li>
            <li>{t("auth.demoDoesNotItem3")}</li>
            <li>{t("auth.demoDoesNotItem4")}</li>
          </ul>
        </div>
      </div>

      {/* REQUEST ACCESS (replaces the old public code box) */}
      <div style={styles.requestBox}>
        <p style={styles.requestLabel}>{t("auth.restrictedDemoAccess")}</p>
        <p style={styles.requestText}>
          {t("auth.requestAccessText")}
        </p>
        <RequestAccessLink t={t} />
      </div>

      {/* INPUT — for people who already have a code */}
      <p style={styles.haveCodeLabel}>{t("auth.haveCode")}</p>
      <input
        type="text"
        placeholder={t("auth.codePlaceholder")}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        onKeyPress={handleKeyPress}
        style={styles.input}
        autoComplete="off"
        spellCheck={false}
      />

      <button onClick={verifyCode} style={styles.button} disabled={checking}>
        {checking ? t("auth.checking") : t("auth.verifyButton")}
      </button>

      {msg && <p style={styles.error}>{msg}</p>}

      <div style={styles.footerNote}>
        {t("auth.footerNote")}
      </div>

      <div style={styles.contactNote}>
        {t("auth.contactNote")} <ContactLink />
      </div>

    </div>
  );
}

function RequestAccessLink({ t }) {
  const mailHref =
    "mailto:contact@fentasafe.org?subject=FentaSafe%20Demo%20Access%20Request";
  return React.createElement(
    "a",
    { href: mailHref, style: styles.requestButton },
    t("auth.requestDemoAccess")
  );
}

function ContactLink() {
  return React.createElement(
    "a",
    { href: "mailto:contact@fentasafe.org", style: styles.link },
    "contact@fentasafe.org"
  );
}

export default OtpLogin;

/* ================= STYLES ================= */
const styles = {
  container: {
    minHeight: "100vh",
    background: "#0f172a",
    color: "#ffffff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "40px",
    paddingBottom: "30px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },

  languageToggleRow: {
    alignSelf: "flex-end",
    marginBottom: "10px",
  },

  lockLabel: {
    fontSize: "11px",
    letterSpacing: "3px",
    color: "#64748b",
    marginBottom: "10px",
    textTransform: "uppercase",
  },

  title: {
    fontSize: "26px",
    fontWeight: "700",
    marginBottom: "10px",
    textAlign: "center",
  },

  subtitle: {
    maxWidth: "580px",
    fontSize: "14px",
    opacity: 0.85,
    lineHeight: "1.6",
    textAlign: "center",
    marginBottom: "20px",
  },

  twoColumnRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    maxWidth: "780px",
    width: "100%",
    marginBottom: "20px",
  },

  card: {
    background: "#020617",
    padding: "16px 18px",
    borderRadius: "10px",
    border: "1px solid #1e293b",
    fontSize: "13.5px",
    lineHeight: "1.6",
  },

  cardTitle: {
    fontSize: "14px",
    fontWeight: "700",
    marginBottom: "10px",
    color: "#e2e8f0",
  },

  requestBox: {
    background: "rgba(37, 99, 235, 0.08)",
    border: "1px solid rgba(37, 99, 235, 0.3)",
    borderRadius: "10px",
    padding: "20px 24px",
    textAlign: "center",
    marginBottom: "20px",
    maxWidth: "380px",
    width: "100%",
  },

  requestLabel: {
    fontSize: "13px",
    fontWeight: "700",
    color: "#94a3b8",
    marginBottom: "8px",
  },

  requestText: {
    fontSize: "12.5px",
    color: "#64748b",
    lineHeight: "1.5",
    marginBottom: "14px",
  },

  requestButton: {
    display: "inline-block",
    width: "100%",
    boxSizing: "border-box",
    padding: "11px 0",
    background: "#2563eb",
    color: "#ffffff",
    borderRadius: "8px",
    fontWeight: "700",
    fontSize: "14px",
    textDecoration: "none",
  },

  haveCodeLabel: {
    fontSize: "12.5px",
    color: "#64748b",
    marginBottom: "8px",
  },

  input: {
    padding: "10px",
    fontSize: "16px",
    width: "240px",
    textAlign: "center",
    borderRadius: "8px",
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    marginBottom: "10px",
    letterSpacing: "2px",
  },

  button: {
    padding: "12px 28px",
    background: "#2563eb",
    color: "#fff",
    borderRadius: "8px",
    border: "none",
    fontWeight: "700",
    fontSize: "15px",
    cursor: "pointer",
  },

  error: {
    color: "#f87171",
    fontSize: "13px",
    marginTop: "8px",
    textAlign: "center",
  },

  footerNote: {
    marginTop: "16px",
    fontSize: "12px",
    opacity: 0.5,
    textAlign: "center",
  },

  contactNote: {
    marginTop: "10px",
    fontSize: "12.5px",
    color: "#64748b",
    textAlign: "center",
  },

  link: {
    color: "#60a5fa",
    textDecoration: "none",
  },
};